import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';
import { api } from '../services/api';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/reports.css';
import { cleanName } from '../utils/names';
import { getBranding } from '../config/branding';
// import GeoMap from './GeoMap'; // Disabled for now

// Types
interface Campaign {
  id: string;
  name: string;
  advertiser: string;
  io_count: number;
  archived?: boolean;
}

interface InsertionOrder {
  id: string;
  name: string;
  archived?: boolean;
}

interface CampaignInfo {
  campaign: {
    id: string;
    name: string;
    advertiser: string;
  };
  insertion_orders: InsertionOrder[];
  io_count: number;
}

interface PerformanceData {
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
  reach?: number;
  spend?: number;
  conversions?: number;
  video_starts?: number;
  video_q25?: number;
  video_q50?: number;
  video_q75?: number;
  video_completions?: number;
  // The video half of the day on a mixed campaign — what VTR and CPV divide by.
  video_impressions?: number;
  video_spend?: number;
  vtr?: number;
  cpv?: number | null;
}

interface UrlData {
  url: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions?: number;
}

interface CreativeData {
  name: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions?: number;
}

interface GeoData {
  country: string;
  region: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

interface DeviceData {
  device: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

type ReportType = 'daily' | 'url' | 'creative' | 'geo' | 'device';
type ReportLanguage = 'ru' | 'uk' | 'en';
type ChartMetric = 'impressions' | 'clicks' | 'ctr' | 'reach';

const REPORT_TYPES: { type: ReportType; labelKey: string; icon: string }[] = [
  { type: 'daily', labelKey: 'reports.typeDaily', icon: 'M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z' },
  { type: 'url', labelKey: 'reports.typeUrl', icon: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' },
  { type: 'creative', labelKey: 'reports.typeCreative', icon: 'M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 4h2v5l-1-.75L9 9V4zm9 16H6V4h1v9l3-2.25L13 13V4h5v16z' },
  { type: 'geo', labelKey: 'reports.typeGeo', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z' },
  { type: 'device', labelKey: 'reports.typeDevice', icon: 'M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm0 18H7V5h10v14z' },
];

const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toFixed(0);
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€', USD: '$', UAH: '₴', GBP: '£', PLN: 'zł', KZT: '₸',
};

const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedAdvertiserId, isAllClients } = useAdvertiser();
  const { user } = useAuth();

  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Campaign[]>([]);
  const [suggestedCampaigns, setSuggestedCampaigns] = useState<Campaign[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIO, setSelectedIO] = useState('all');
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState<ReportType>('daily');
  // A report is written in the language its reader is already reading the
  // platform in. No separate choice: the one in Settings decides both.
  const reportLanguage: ReportLanguage =
    (['ru', 'uk', 'en'] as ReportLanguage[]).find(l => l === i18n.language) || 'en';
  const [activePeriod, setActivePeriod] = useState<string>('lastweek');
  const [showResults, setShowResults] = useState(false);
  const [loadedReportType, setLoadedReportType] = useState<ReportType>('daily');
  const [previewData, setPreviewData] = useState<PerformanceData[]>([]);
  // Money in the report is already resolved per viewer role by the backend — a
  // client sees the client price, an agency its own — and converted to the
  // currency that client is billed in, so it matches the campaign card.
  const [reportCurrency, setReportCurrency] = useState<string>('EUR');

  // A dash rather than a zero where the divisor is missing: no clicks means CPC is
  // undefined, not free.
  const money = (v: number | null | undefined): string =>
    v === null || v === undefined
      ? '-'
      : `${CURRENCY_SYMBOL[reportCurrency] || reportCurrency} ${v.toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const perUnit = (spend?: number, n?: number): number | null =>
    spend !== undefined && n ? spend / n : null;
  const perMille = (spend?: number, impressions?: number): number | null =>
    spend !== undefined && impressions ? (spend / impressions) * 1000 : null;
  // CPA is meaningless without conversions, so the column only appears when the
  // period actually has some.
  const hasConversions = previewData.some((r) => (r.conversions || 0) > 0);
  // Display buys report zeros for video, so the two columns only appear where
  // something was actually watched — no column of noughts on a banner report.
  const hasVideo = previewData.some((r) => (r.video_completions || 0) > 0);
  // Period VTR is completions over impressions for the whole period — not a mean
  // of the daily rates, which would weigh a slow day like a heavy one.
  const videoStats = (() => {
    const sum = (f: (r: PerformanceData) => number | undefined) =>
      previewData.reduce((acc, r) => acc + (f(r) || 0), 0);
    const completions = sum((r) => r.video_completions);
    // Video impressions and video spend, not the campaign's: on a mixed buy the
    // banners would water down VTR and inflate the cost per view.
    const impressions = sum((r) => r.video_impressions);
    const spend = sum((r) => r.video_spend);
    return {
      starts: sum((r) => r.video_starts),
      q25: sum((r) => r.video_q25),
      q50: sum((r) => r.video_q50),
      q75: sum((r) => r.video_q75),
      completions,
      impressions,
      vtr: impressions > 0 ? (completions / impressions) * 100 : 0,
      // Cost per completed view — VTR and CPV both count the 100% mark only.
      cpv: completions > 0 ? spend / completions : null,
    };
  })();
  const [urlData, setUrlData] = useState<UrlData[]>([]);
  const [creativeData, setCreativeData] = useState<CreativeData[]>([]);
  const [geoData, setGeoData] = useState<GeoData[]>([]);
  const [deviceData, setDeviceData] = useState<DeviceData[]>([]);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('impressions');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const [sortColumn, setSortColumn] = useState<string>('');
  // Aggregate totals from API (for paginated reports)
  const [aggregateTotals, setAggregateTotals] = useState<{ impressions: number; clicks: number; ctr: number }>({
    impressions: 0,
    clicks: 0,
    ctr: 0
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const chartDataRef = useRef<{ points: any[], padding: any } | null>(null);

  // Set default dates on mount
  useEffect(() => {
    setDefaultDates();
  }, []);

  // Load suggested campaigns on mount
  useEffect(() => {
    const loadSuggestedCampaigns = async () => {
      try {
        const advParam = !isAllClients ? `?advertiser_id=${selectedAdvertiserId}` : '';
        const response = await api.get(`/api/reports/campaigns/suggested${advParam}`);
        setSuggestedCampaigns(response.data);
      } catch (err) {
        console.error('Error loading suggested campaigns:', err);
      }
    };
    loadSuggestedCampaigns();
  }, [selectedAdvertiserId, isAllClients]);

  // Load campaign info when campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      loadCampaignInfo(selectedCampaign);
    }
  }, [selectedCampaign]);

  // Redraw chart when data or metric changes
  useEffect(() => {
    if (showResults && previewData.length > 0) {
      drawChart();
    }
  }, [previewData, chartMetric, showResults]);

  // Redraw chart on window resize
  useEffect(() => {
    const handleResize = () => {
      if (showResults && previewData.length > 0) {
        drawChart();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showResults, previewData]);

  // Redraw when the canvas box itself changes size (e.g. edition CSS height),
  // so the internal buffer always matches the rendered box (no stretch).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !showResults || previewData.length === 0) return;
    const ro = new ResizeObserver(() => drawChart());
    ro.observe(canvas);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults, previewData]);

  const setDefaultDates = () => {
    // Set to "Last Week" - Monday to Sunday of previous week
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const dayOfWeek = now.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const lastMonday = new Date(year, month, day - daysSinceMonday - 7);
    const lastSunday = new Date(year, month, day - daysSinceMonday - 1);

    const formatDate = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    setDateFrom(formatDate(lastMonday));
    setDateTo(formatDate(lastSunday));
  };

  const loadCampaignInfo = async (campaignId: string) => {
    try {
      const response = await api.get(`/api/reports/campaign/${campaignId}/info`);
      setCampaignInfo(response.data);
      setSelectedIO('all'); // Reset IO selection when campaign changes
    } catch (err) {
      console.error('Error loading campaign info:', err);
    }
  };

  // Search campaigns with debounce
  const searchCampaigns = async (query: string) => {
    if (query.length < 1) {
      setSearchResults([]);
      // Don't close dropdown - show suggested campaigns instead
      return;
    }

    setSearchLoading(true);
    try {
      const params: any = { q: query, limit: 20 };
      if (!isAllClients) params.advertiser_id = selectedAdvertiserId;
      const response = await api.get(`/api/reports/campaigns/search`, { params });
      setSearchResults(response.data);
      setShowDropdown(true);
    } catch (err) {
      console.error('Error searching campaigns:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      searchCampaigns(value);
    }, 300);
  };

  // Handle campaign selection from dropdown
  const handleCampaignSelect = (campaign: Campaign) => {
    setSelectedCampaign(campaign.id);
    setSearchQuery(`${campaign.advertiser} / ${cleanName(campaign.name)}`);
    setShowDropdown(false);
    // Add to campaigns list if not already there
    if (!campaigns.find(c => c.id === campaign.id)) {
      setCampaigns(prev => [...prev, campaign]);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear campaign selection
  const handleClearCampaign = () => {
    setSelectedCampaign('');
    setSearchQuery('');
    setSearchResults([]);
    setCampaignInfo(null);
  };

  const loadPreviewData = async (page: number = 1) => {
    if (!selectedCampaign || !dateFrom || !dateTo) return;

    try {
      let endpoint = '';
      switch (reportType) {
        case 'daily':
          endpoint = `/api/reports/campaign-preview`;
          break;
        case 'url':
          endpoint = `/api/reports/url-preview`;
          break;
        case 'creative':
          endpoint = `/api/reports/creative-preview`;
          break;
        case 'geo':
          endpoint = `/api/reports/geo-preview`;
          break;
        case 'device':
          endpoint = `/api/reports/device-preview`;
          break;
      }

      const params: any = {
        campaign_id: selectedCampaign,
        date_from: dateFrom,
        date_to: dateTo
      };
      if (selectedIO !== 'all') {
        params.io_id = selectedIO;
      }
      // Add pagination and sorting for non-daily reports
      if (reportType !== 'daily') {
        params.page = page;
        params.page_size = pageSize;
        if (sortColumn) {
          params.sort_column = sortColumn;
          params.sort_direction = sortDirection;
        }
      }

      const response = await api.get(endpoint, { params });

      if (response.data && response.data.data) {
        switch (reportType) {
          case 'daily':
            setPreviewData(response.data.data);
            setReportCurrency(response.data.currency || 'EUR');
            break;
          case 'url':
            setUrlData(response.data.data);
            break;
          case 'creative':
            setCreativeData(response.data.data);
            break;
          case 'geo':
            setGeoData(response.data.data);
            break;
          case 'device':
            setDeviceData(response.data.data);
            break;
        }
        setLoadedReportType(reportType);
        // Reset sorting when report type changes
        setSortColumn('');
        setSortDirection('desc');

        // Set pagination info
        if (response.data.total_pages !== undefined) {
          setTotalPages(response.data.total_pages);
          setTotalCount(response.data.total_count);
          setCurrentPage(page);
          // Store aggregate totals from API for paginated reports
          if (response.data.total_impressions !== undefined) {
            setAggregateTotals({
              impressions: response.data.total_impressions,
              clicks: response.data.total_clicks,
              ctr: response.data.total_ctr || 0
            });
          }
        } else {
          setTotalPages(1);
          setTotalCount(response.data.data.length);
          setCurrentPage(1);
          // Reset aggregate totals for non-paginated (daily) reports
          setAggregateTotals({ impressions: 0, clicks: 0, ctr: 0 });
        }
      } else {
        setPreviewData([]);
        setUrlData([]);
        setCreativeData([]);
        setGeoData([]);
        setDeviceData([]);
        setTotalPages(1);
        setTotalCount(0);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Error loading preview data:', err);
      setPreviewData([]);
      setUrlData([]);
      setCreativeData([]);
      setGeoData([]);
      setDeviceData([]);
      setAggregateTotals({ impressions: 0, clicks: 0, ctr: 0 });
      setError(t('reports.previewError'));
    }
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      await loadPreviewData(newPage);
    }
  };

  const handleSort = async (column: string) => {
    const newDirection = sortColumn === column
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);

    // Re-fetch data with new sorting (page 1)
    if (reportType !== 'daily') {
      setCurrentPage(1);
      // Load with new sort params - need to pass them directly since state update is async
      try {
        let endpoint = '';
        switch (reportType) {
          case 'url':
            endpoint = `/api/reports/url-preview`;
            break;
          case 'creative':
            endpoint = `/api/reports/creative-preview`;
            break;
          case 'geo':
            endpoint = `/api/reports/geo-preview`;
            break;
          case 'device':
            endpoint = `/api/reports/device-preview`;
            break;
        }

        const params: any = {
          campaign_id: selectedCampaign,
          date_from: dateFrom,
          date_to: dateTo,
          page: 1,
          page_size: pageSize,
          sort_column: column,
          sort_direction: newDirection
        };
        if (selectedIO !== 'all') {
          params.io_id = selectedIO;
        }

        const response = await api.get(endpoint, { params });

        if (response.data && response.data.data) {
          switch (reportType) {
            case 'url':
              setUrlData(response.data.data);
              break;
            case 'creative':
              setCreativeData(response.data.data);
              break;
            case 'geo':
              setGeoData(response.data.data);
              break;
            case 'device':
              setDeviceData(response.data.data);
              break;
          }
          if (response.data.total_pages !== undefined) {
            setTotalPages(response.data.total_pages);
            setTotalCount(response.data.total_count);
            setCurrentPage(1);
            if (response.data.total_impressions !== undefined) {
              setAggregateTotals({
                impressions: response.data.total_impressions,
                clicks: response.data.total_clicks,
                ctr: response.data.total_ctr || 0
              });
            }
          }
        }
      } catch (err) {
        console.error('Error sorting data:', err);
      }
    }
  };

  const sortData = <T extends Record<string, any>>(data: T[], column: string): T[] => {
    if (!column) return data;
    return [...data].sort((a, b) => {
      const aVal = a[column];
      const bVal = b[column];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  };

  const SortIcon = ({ column }: { column: string }) => (
    <span style={{ marginLeft: '6px', opacity: sortColumn === column ? 1 : 0.3 }}>
      {sortColumn === column && sortDirection === 'asc' ? '↑' : '↓'}
    </span>
  );

  const ThSortable = ({ column, children, className }: { column: string; children: React.ReactNode; className?: string }) => (
    <th
      onClick={() => handleSort(column)}
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {children}
      <SortIcon column={column} />
    </th>
  );

  const handleShowReport = async () => {
    if (!selectedCampaign || !dateFrom || !dateTo) {
      setError(t('reports.selectCampaignAndRange'));
      return;
    }
    // Clear previous data before loading new data
    setPreviewData([]);
    setUrlData([]);
    setCreativeData([]);
    setGeoData([]);
    setDeviceData([]);
    setAggregateTotals({ impressions: 0, clicks: 0, ctr: 0 });

    await loadPreviewData();
    setShowResults(true);
    setTimeout(() => {
      document.getElementById('resultsArea')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleDownload = async () => {
    if (!selectedCampaign || !dateFrom || !dateTo) {
      setError(t('reports.selectCampaignAndRange'));
      return;
    }

    setDownloading(true);
    setError('');

    try {
      let url = '';
      let params: any = {};

      switch (reportType) {
        case 'daily':
          url = `/api/reports/download`;
          params = { campaign_id: selectedCampaign, date_from: dateFrom, date_to: dateTo, lang: reportLanguage };
          break;
        case 'url':
          url = `/reports/url/export`;
          params = { campaign_id: selectedCampaign, start_date: dateFrom, end_date: dateTo, lang: reportLanguage };
          break;
        case 'creative':
          url = `/reports/creative/export`;
          params = { campaign_id: selectedCampaign, start_date: dateFrom, end_date: dateTo, lang: reportLanguage };
          break;
        case 'geo':
          url = `/reports/geo/export`;
          params = { campaign_id: selectedCampaign, start_date: dateFrom, end_date: dateTo, lang: reportLanguage };
          break;
        case 'device':
          url = `/reports/device/export`;
          params = { campaign_id: selectedCampaign, start_date: dateFrom, end_date: dateTo, lang: reportLanguage };
          break;
      }

      const response = await api.get(url, { params, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${reportType}_report_${dateFrom}_${dateTo}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setError('Error generating report: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloading(false);
    }
  };

  const handlePeriodClick = (period: string) => {
    setActivePeriod(period);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ...

    // Days since Monday (Monday = 0, Sunday = 6)
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const start = new Date(year, month, day);
    const end = new Date(year, month, day);

    switch (period) {
      case 'week':
        // This week: Monday of current week to today
        start.setDate(day - daysSinceMonday);
        // end stays as today
        break;
      case 'lastweek':
        // Last week: Monday to Sunday of previous week
        start.setDate(day - daysSinceMonday - 7);
        end.setDate(day - daysSinceMonday - 1);
        break;
      case 'month':
        // Last 30 days
        start.setDate(day - 30);
        break;
    }

    const formatDate = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    setDateFrom(formatDate(start));
    setDateTo(formatDate(end));
  };

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || previewData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    // Адаптивные отступы для мобильных
    const isMobile = width < 500;
    const padding = isMobile
      ? { top: 10, right: 10, bottom: 25, left: 35 }
      : { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Get data for current metric
    const data = previewData.map(d => {
      switch (chartMetric) {
        case 'impressions': return d.impressions;
        case 'clicks': return d.clicks;
        case 'ctr': return d.ctr;
        case 'reach': return d.reach || 0;
        default: return d.impressions;
      }
    });

    const maxValue = Math.max(...data);
    const dataMin = Math.min(...data);
    // Add padding below minimum value to show real proportions
    // Use 150% of data range as padding below dataMin, but not less than 0
    const minValue = Math.max(0, dataMin - (maxValue - dataMin) * 1.5);
    const range = maxValue - minValue || 1;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // Y-axis labels
    ctx.fillStyle = '#aaa';
    const _axisFont = getBranding().edition !== 'sense' ? "'JetBrains Mono', monospace" : 'DM Sans, sans-serif';
    ctx.font = isMobile ? `9px ${_axisFont}` : `12px ${_axisFont}`;
    ctx.textAlign = 'right';
    const yLabelCount = isMobile ? 3 : 5; // Меньше меток на мобильных
    for (let i = 0; i <= yLabelCount; i++) {
      const value = maxValue - (range / yLabelCount) * i;
      const y = padding.top + (chartHeight / yLabelCount) * i;
      const label = chartMetric === 'ctr' ? value.toFixed(2) + '%' : formatNumber(value);
      ctx.fillText(label, padding.left - (isMobile ? 3 : 10), y + 4);
    }

    // Calculate points
    const points = data.map((value, index) => {
      const x = padding.left + (chartWidth / (data.length - 1 || 1)) * index;
      const normalizedValue = (value - minValue) / range;
      const y = padding.top + chartHeight - (normalizedValue * chartHeight);
      return { x, y, value, date: previewData[index].date };
    });

    chartDataRef.current = { points, padding };

    // Edition-aware chart colors. Dievo (per reports.html): dark-olive line
    // #3E5310, lime area fill #C8F032, white dots with dark stroke.
    const dievoChart = getBranding().edition !== 'sense';
    const cLine = dievoChart ? '#3E5310' : '#FF6B4A';
    const cShadow = dievoChart ? 'rgba(62, 83, 16, 0.18)' : 'rgba(255, 107, 74, 0.3)';

    // Gradient fill
    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    if (dievoChart) {
      gradient.addColorStop(0, 'rgba(200, 240, 50, 0.55)');
      gradient.addColorStop(0.6, 'rgba(200, 240, 50, 0.22)');
      gradient.addColorStop(1, 'rgba(200, 240, 50, 0.04)');
    } else {
      gradient.addColorStop(0, 'rgba(255, 107, 74, 0.18)');
      gradient.addColorStop(0.4, 'rgba(233, 75, 108, 0.12)');
      gradient.addColorStop(0.8, 'rgba(142, 58, 154, 0.08)');
      gradient.addColorStop(1, 'rgba(142, 58, 154, 0.02)');
    }

    // Handle single point case - just draw the point, no curves
    if (points.length === 1) {
      // Draw a vertical gradient bar for single point
      const point = points[0];
      const barWidth = 40;
      ctx.save();
      ctx.fillStyle = gradient;
      ctx.fillRect(point.x - barWidth / 2, point.y, barWidth, height - padding.bottom - point.y);
      ctx.restore();
    } else {
      // Calculate tangents for monotone cubic interpolation
      const tangents: number[] = [];
      for (let i = 0; i < points.length; i++) {
        if (i === 0) {
          tangents[i] = (points[1].y - points[0].y) / (points[1].x - points[0].x);
        } else if (i === points.length - 1) {
          tangents[i] = (points[i].y - points[i - 1].y) / (points[i].x - points[i - 1].x);
        } else {
          tangents[i] = ((points[i + 1].y - points[i - 1].y) / (points[i + 1].x - points[i - 1].x)) * 0.5;
        }
      }

      // Helper function to draw smooth curve through points
      const drawSmoothCurve = () => {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 0; i < points.length - 1; i++) {
          const p1 = points[i];
          const p2 = points[i + 1];
          const t1 = tangents[i];
          const t2 = tangents[i + 1];

          const dx = (p2.x - p1.x) / 3;
          const cp1x = p1.x + dx;
          const cp1y = p1.y + t1 * dx;
          const cp2x = p2.x - dx;
          const cp2y = p2.y - t2 * dx;

          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
        }
      };

      // Draw fill
      ctx.save();
      drawSmoothCurve();
      ctx.lineTo(points[points.length - 1].x, height - padding.bottom);
      ctx.lineTo(points[0].x, height - padding.bottom);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.restore();

      // Draw line
      ctx.save();
      drawSmoothCurve();
      ctx.strokeStyle = cLine;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (!dievoChart) {
        ctx.shadowColor = cShadow;
        ctx.shadowBlur = 8;
      }
      ctx.stroke();
      ctx.restore();
    }

    // Draw points - small and elegant
    const pointSize = isMobile ? 3 : 4;
    const centerDotSize = isMobile ? 1 : 1.5;
    points.forEach(point => {
      // Subtle shadow (Sense only — Dievo dots are flat hollow circles)
      ctx.save();
      if (!dievoChart) {
        ctx.shadowColor = cShadow;
        ctx.shadowBlur = isMobile ? 4 : 6;
      }

      // White outer circle
      ctx.beginPath();
      ctx.arc(point.x, point.y, pointSize, 0, Math.PI * 2);
      ctx.fillStyle = 'white';
      ctx.fill();
      ctx.restore();

      // Thin accent ring
      ctx.beginPath();
      ctx.arc(point.x, point.y, pointSize, 0, Math.PI * 2);
      ctx.strokeStyle = cLine;
      ctx.lineWidth = isMobile ? 1 : 1.5;
      ctx.stroke();

      // Tiny center dot — Sense only (Dievo dots stay hollow)
      if (!dievoChart) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, centerDotSize, 0, Math.PI * 2);
        ctx.fillStyle = cLine;
        ctx.fill();
      }
    });

    // X-axis labels
    ctx.fillStyle = '#aaa';
    const _axisFont2 = getBranding().edition !== 'sense' ? "'JetBrains Mono', monospace" : 'DM Sans, sans-serif';
    ctx.font = isMobile ? `8px ${_axisFont2}` : `12px ${_axisFont2}`;
    ctx.textAlign = 'center';
    const xLabelInterval = isMobile ? Math.ceil(points.length / 4) : Math.ceil(points.length / 8);
    points.forEach((point, i) => {
      if (i % xLabelInterval === 0 || i === points.length - 1) {
        ctx.fillText(formatDate(point.date).slice(0, 5), point.x, height - padding.bottom + (isMobile ? 12 : 20));
      }
    });
  }, [previewData, chartMetric]);

  // Handle mouse move for tooltip
  const handleChartMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartDataRef.current || !tooltipRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const { points } = chartDataRef.current;
    let foundPoint: any = null;

    // Find closest point
    let minDistance = Infinity;
    points.forEach(point => {
      const distance = Math.sqrt(Math.pow(mouseX - point.x, 2) + Math.pow(mouseY - point.y, 2));
      if (distance < 20 && distance < minDistance) {
        foundPoint = point;
        minDistance = distance;
      }
    });

    const tooltip = tooltipRef.current;

    if (foundPoint) {
      const metricLabels: Record<ChartMetric, string> = {
        impressions: t('reports.impressions'),
        clicks: t('reports.clicks'),
        ctr: 'CTR',
        reach: t('reports.reach')
      };

      const formattedValue = chartMetric === 'ctr'
        ? foundPoint.value.toFixed(2) + '%'
        : foundPoint.value.toLocaleString(currentLocale());

      tooltip.innerHTML = `
        <div class="tooltip-date">${formatDate(foundPoint.date)}</div>
        <div class="tooltip-value">${formattedValue}</div>
        <div class="tooltip-label">${metricLabels[chartMetric]}</div>
      `;

      tooltip.style.left = (e.clientX - rect.left + 15) + 'px';
      tooltip.style.top = (e.clientY - rect.top - 60) + 'px';
      tooltip.classList.add('show');
      canvas.style.cursor = 'pointer';
    } else {
      tooltip.classList.remove('show');
      canvas.style.cursor = 'default';
    }
  }, [chartMetric]);

  const handleChartMouseLeave = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.classList.remove('show');
    }
  }, []);

  const getTotalStats = () => {
    // For paginated reports (URL, creative, geo, device), use aggregate totals from API
    // This ensures we show totals for ALL data, not just the current page
    if (loadedReportType !== 'daily' && aggregateTotals.impressions > 0) {
      return aggregateTotals;
    }

    // For daily reports (non-paginated), sum all displayed data
    let data: { impressions: number; clicks: number }[] = [];

    switch (loadedReportType) {
      case 'daily':
        data = previewData;
        break;
      case 'url':
        data = urlData;
        break;
      case 'creative':
        data = creativeData;
        break;
      case 'geo':
        data = geoData;
        break;
      case 'device':
        data = deviceData;
        break;
    }

    if (data.length === 0) return { impressions: 0, clicks: 0, ctr: 0 };

    const total = data.reduce(
      (acc, d) => ({
        impressions: acc.impressions + d.impressions,
        clicks: acc.clicks + d.clicks,
      }),
      { impressions: 0, clicks: 0 }
    );
    return {
      ...total,
      ctr: total.impressions > 0 ? (total.clicks / total.impressions) * 100 : 0,
    };
  };

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);
  const totalStats = getTotalStats();

  // Money exists only on the daily report: the breakdown fact tables (geo, url,
  // creative, device) carry impressions and clicks but no cost at all, so there is
  // nothing to divide by there. Derived from the totals, not averaged per day.
  const dailyMoney = (() => {
    if (loadedReportType !== 'daily' || previewData.length === 0) return null;
    const spend = previewData.reduce((s, r) => s + (r.spend || 0), 0);
    if (!spend) return null;
    const impressions = previewData.reduce((s, r) => s + r.impressions, 0);
    const clicks = previewData.reduce((s, r) => s + r.clicks, 0);
    const conversions = previewData.reduce((s, r) => s + (r.conversions || 0), 0);
    return {
      spend,
      cpm: perMille(spend, impressions),
      cpc: perUnit(spend, clicks),
      cpa: perUnit(spend, conversions),
    };
  })();

  // Check if we have any data to display
  const hasData = () => {
    switch (loadedReportType) {
      case 'daily':
        return previewData.length > 0;
      case 'url':
        return urlData.length > 0;
      case 'creative':
        return creativeData.length > 0;
      case 'geo':
        return geoData.length > 0;
      case 'device':
        return deviceData.length > 0;
      default:
        return false;
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('reports.title')}</h1>
          <p className="header-subtitle">{t('reports.subtitle')}</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('reports.errorLabel')}</strong> {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Main Layout */}
      <div className="reports-layout">
        {/* Filters Panel */}
        <aside className="filters-panel">
          {/* Report Type */}
          <div className="filter-section">
            <h3 className="section-heading">{t('reports.reportType')}</h3>
            <div className="type-segmented">
              {REPORT_TYPES.map((rt) => (
                <button
                  key={rt.type}
                  className={`type-segment ${reportType === rt.type ? 'active' : ''}`}
                  onClick={() => setReportType(rt.type)}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d={rt.icon} />
                  </svg>
                  <span className="type-segment-label">{t(rt.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Campaign */}
          <div className="filter-section">
            <div className="form-field">
              <label className="section-heading">{t('reports.campaign')}</label>
              <div className="campaign-search-container" ref={dropdownRef}>
                <div className="campaign-search-input-wrapper">
                  <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    className="campaign-search-input"
                    placeholder={t('reports.searchCampaigns')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => {
                      setShowDropdown(true);
                    }}
                  />
                  {searchLoading && (
                    <div className="search-spinner"></div>
                  )}
                  {selectedCampaign && !searchLoading && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={handleClearCampaign}
                      title={t('reports.clearSelection')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"></path>
                      </svg>
                    </button>
                  )}
                </div>
                {showDropdown && !searchLoading && (searchQuery.length >= 1 || suggestedCampaigns.length > 0) && (
                  <div className="campaign-search-dropdown">
                    {/* Show search results if user is searching */}
                    {searchQuery.length >= 1 && searchResults.length > 0 && searchResults.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`campaign-search-item ${selectedCampaign === campaign.id ? 'selected' : ''}`}
                        onClick={() => handleCampaignSelect(campaign)}
                      >
                        <div className="campaign-search-item-name">
                          {cleanName(campaign.name)}
                          {campaign.archived && <span className="archived-label">(Archived)</span>}
                        </div>
                        <div className="campaign-search-item-advertiser">
                          {campaign.advertiser} · {t('campaigns.adGroupsCount', { count: campaign.io_count })}
                        </div>
                      </div>
                    ))}
                    {/* Show suggested campaigns if search is empty */}
                    {searchQuery.length === 0 && suggestedCampaigns.length > 0 && suggestedCampaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`campaign-search-item ${selectedCampaign === campaign.id ? 'selected' : ''}`}
                        onClick={() => handleCampaignSelect(campaign)}
                      >
                        <div className="campaign-search-item-name">
                          {cleanName(campaign.name)}
                        </div>
                        <div className="campaign-search-item-advertiser">
                          {campaign.advertiser} · {t('campaigns.adGroupsCount', { count: campaign.io_count })}
                        </div>
                      </div>
                    ))}
                    {/* No results message */}
                    {searchQuery.length >= 1 && searchResults.length === 0 && (
                      <div className="campaign-search-empty">
                        No campaigns found for "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* IO (Group) Selector */}
            {selectedCampaign && campaignInfo && (
              <div className="form-field">
                <label className="section-heading">{t('reports.adGroup')}</label>
                <select
                  className="field-select"
                  value={selectedIO}
                  onChange={(e) => setSelectedIO(e.target.value)}
                >
                  <option value="all">{t('reports.entireCampaign')}</option>
                  {campaignInfo.insertion_orders.map((io) => (
                    <option key={io.id} value={io.id}>
                      {cleanName(io.name)}{io.archived ? ' (Archived)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Period */}
          <div className="filter-section">
            <h3 className="section-heading">{t('reports.period')}</h3>
            <div className="period-buttons">
              <button
                className={`period-button ${activePeriod === 'week' ? 'active' : ''}`}
                onClick={() => handlePeriodClick('week')}
              >
                {t('reports.thisWeek')}
              </button>
              <button
                className={`period-button ${activePeriod === 'lastweek' ? 'active' : ''}`}
                onClick={() => handlePeriodClick('lastweek')}
              >
                {t('reports.lastWeek')}
              </button>
              <button
                className={`period-button ${activePeriod === 'month' ? 'active' : ''}`}
                onClick={() => handlePeriodClick('month')}
              >
                {t('reports.lastMonth')}
              </button>
            </div>
            <div className="date-fields">
              <div className="form-field">
                <label className="field-label">{t('reports.start')}</label>
                <input
                  type="date"
                  className="date-input"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                />
              </div>
              <div className="form-field">
                <label className="field-label">{t('reports.end')}</label>
                <input
                  type="date"
                  className="date-input"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="filter-section">
            <div className="action-buttons">
              <button
                className="btn-primary"
                onClick={handleShowReport}
                disabled={!selectedCampaign || !dateFrom || !dateTo}
              >
                {getBranding().edition !== 'sense'
                  ? <span className="dot"></span>
                  : (
                    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                    </svg>
                  )}
                {t('reports.showReport')}
              </button>
              <button
                className="btn-secondary"
                onClick={handleDownload}
                disabled={!selectedCampaign || !dateFrom || !dateTo || downloading}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />
                </svg>
                {downloading ? t('reports.downloading') : t('reports.downloadExcel')}
              </button>
            </div>
          </div>
        </aside>

        {/* Results Area */}
        <div className={`results-area ${showResults ? 'visible' : ''}`} id="resultsArea">
          <div className="report-card">
            <div className="report-header">
              <div className="report-title">
                {t('reports.reportTitle', {
                  type: t(REPORT_TYPES.find(rt => rt.type === loadedReportType)?.labelKey || 'reports.typeDaily'),
                })}
              </div>
              <div className="report-period">
                {formatDate(dateFrom)} - {formatDate(dateTo)}
              </div>
            </div>

            {/* No Data Message */}
            {!hasData() && (
              <div style={{
                padding: '60px 40px',
                textAlign: 'center',
                color: '#6c757d'
              }}>
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#adb5bd"
                  strokeWidth="1.5"
                  style={{ marginBottom: '16px' }}
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#495057', marginBottom: '8px' }}>
                  {t('reports.noData')}
                </div>
                <div style={{ fontSize: '14px' }}>
                  {t('reports.noDataHint')}
                </div>
              </div>
            )}

            {/* Summary Stats - only show when we have data */}
            {hasData() && (
              <div className="report-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                  <div className="report-summary-value" style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a' }}>
                    {totalStats.impressions.toLocaleString(currentLocale())}
                  </div>
                  <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>{t('reports.impressions')}</div>
                </div>
                <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                  <div className="report-summary-value" style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a' }}>
                    {totalStats.clicks.toLocaleString(currentLocale())}
                  </div>
                  <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>{t('reports.clicks')}</div>
                </div>
                <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                  <div className="report-summary-value" style={{ fontSize: '28px', fontWeight: 700, color: '#2ecc71' }}>
                    {totalStats.ctr.toFixed(2)}%
                  </div>
                  <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>CTR</div>
                </div>
                {/* Video only: the full watch-throughs and what share of the
                    impressions they are. Both come from the period totals. */}
                {hasVideo && (
                  <>
                    <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                      <div className="report-summary-value" style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a' }}>
                        {videoStats.completions.toLocaleString(currentLocale())}
                      </div>
                      <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>{t('reports.views100')}</div>
                    </div>
                    <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                      <div className="report-summary-value" style={{ fontSize: '28px', fontWeight: 700, color: '#2ecc71' }}>
                        {videoStats.vtr.toFixed(2)}%
                      </div>
                      <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>VTR</div>
                    </div>
                    {videoStats.cpv !== null && (
                      <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                        <div className="report-summary-value" style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                          {money(videoStats.cpv)}
                        </div>
                        <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>CPV</div>
                      </div>
                    )}
                  </>
                )}
                {dailyMoney && (
                  <>
                    <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                      <div className="report-summary-value" style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                        {money(dailyMoney.spend)}
                      </div>
                      <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>{t('reports.budget')}</div>
                    </div>
                    <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                      <div className="report-summary-value" style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                        {money(dailyMoney.cpm)}
                      </div>
                      <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>CPM</div>
                    </div>
                    <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                      <div className="report-summary-value" style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                        {money(dailyMoney.cpc)}
                      </div>
                      <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>CPC</div>
                    </div>
                    {dailyMoney.cpa !== null && (
                      <div className="report-summary-cell" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center' }}>
                        <div className="report-summary-value" style={{ fontSize: '26px', fontWeight: 700, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                          {money(dailyMoney.cpa)}
                        </div>
                        <div className="report-summary-label" style={{ fontSize: '13px', color: '#6c757d', textTransform: 'uppercase' }}>CPA</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* How far the spot was actually watched. Each step is a share of
                impressions, so the four read as one decreasing funnel. */}
            {loadedReportType === 'daily' && hasData() && hasVideo && (
              <div className="report-video-funnel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: '25%', value: videoStats.q25 },
                  { label: '50%', value: videoStats.q50 },
                  { label: '75%', value: videoStats.q75 },
                  { label: '100%', value: videoStats.completions },
                ].map((step) => {
                  const share = videoStats.impressions > 0 ? (step.value / videoStats.impressions) * 100 : 0;  // of video impressions
                  return (
                    <div key={step.label} style={{ padding: '14px 16px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                        {t('reports.viewsSuffix', { pct: step.label })}
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a' }}>{step.value.toLocaleString(currentLocale())}</div>
                      <div style={{ height: '4px', background: '#f1f3f5', borderRadius: '2px', margin: '8px 0 6px' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, share)}%`, background: '#2ecc71', borderRadius: '2px' }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>{t('reports.ofVideoImpressions', { pct: share.toFixed(1) })}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Chart Section - only for Daily reports with data */}
            {loadedReportType === 'daily' && hasData() && (
              <div className="chart-section">
                <div className="chart-header">
                  <div className="chart-title">{t('reports.performanceTrends')}</div>
                  <div className="chart-tabs">
                    {(['impressions', 'clicks', 'ctr', 'reach'] as ChartMetric[]).map((metric) => {
                      const labels: Record<ChartMetric, { full: string; short: string }> = {
                        impressions: { full: t('reports.impressions'), short: t('reports.imprShort') },
                        clicks: { full: t('reports.clicks'), short: t('reports.clicksShort') },
                        ctr: { full: 'CTR', short: 'CTR' },
                        reach: { full: t('reports.reach'), short: t('reports.reachShort') }
                      };
                      return (
                        <button
                          key={metric}
                          className={`chart-tab ${chartMetric === metric ? 'active' : ''}`}
                          onClick={() => setChartMetric(metric)}
                        >
                          <span className="chart-tab-full">{labels[metric].full}</span>
                          <span className="chart-tab-short">{labels[metric].short}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="chart-container">
                  <canvas
                    ref={canvasRef}
                    id="reportChart"
                    style={{ width: '100%', height: '100%' }}
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleChartMouseLeave}
                  />
                  <div ref={tooltipRef} className="chart-tooltip" id="chartTooltip"></div>
                </div>
              </div>
            )}

            {/* Data Tables - only show when we have data */}
            {loadedReportType === 'daily' && hasData() && (
              <div className="report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <ThSortable column="date">{t('reports.date')}</ThSortable>
                      <ThSortable column="impressions">{t('reports.impressions')}</ThSortable>
                      <ThSortable column="clicks">{t('reports.clicks')}</ThSortable>
                      <ThSortable column="ctr">CTR</ThSortable>
                      <ThSortable column="reach">{t('reports.reach')}</ThSortable>
                      {hasVideo && <ThSortable column="video_q25">{t('reports.views25')}</ThSortable>}
                      {hasVideo && <ThSortable column="video_q50">{t('reports.views50')}</ThSortable>}
                      {hasVideo && <ThSortable column="video_q75">{t('reports.views75')}</ThSortable>}
                      {hasVideo && <ThSortable column="video_completions">{t('reports.views100')}</ThSortable>}
                      {hasVideo && <th>VTR</th>}
                      <ThSortable column="conversions">{t('reports.conversions')}</ThSortable>
                      <th>CR</th>
                      <ThSortable column="spend">{t('reports.budget')}</ThSortable>
                      <th>CPM</th>
                      <th>CPC</th>
                      {hasVideo && <th>CPV</th>}
                      {hasConversions && <th>CPA</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortData(previewData, sortColumn).map((row, idx) => (
                      <tr key={idx}>
                        <td className="val-big">{formatDate(row.date)}</td>
                        <td>{row.impressions.toLocaleString(currentLocale())}</td>
                        <td>{row.clicks.toLocaleString(currentLocale())}</td>
                        <td className={row.ctr > 0.8 ? 'val-positive' : ''}>{row.ctr.toFixed(2)}%</td>
                        <td>{row.reach?.toLocaleString(currentLocale()) || '-'}</td>
                        {hasVideo && <td>{row.video_q25?.toLocaleString(currentLocale()) || '-'}</td>}
                        {hasVideo && <td>{row.video_q50?.toLocaleString(currentLocale()) || '-'}</td>}
                        {hasVideo && <td>{row.video_q75?.toLocaleString(currentLocale()) || '-'}</td>}
                        {hasVideo && <td>{row.video_completions?.toLocaleString(currentLocale()) || '-'}</td>}
                        {hasVideo && (
                          <td>{(row.video_impressions || 0) > 0 && (row.video_completions || 0) > 0
                            ? (((row.video_completions || 0) / (row.video_impressions || 1)) * 100).toFixed(2) + '%'
                            : '-'}</td>
                        )}
                        <td>{row.conversions?.toLocaleString(currentLocale()) || '-'}</td>
                        <td>{row.clicks > 0 ? (((row.conversions || 0) / row.clicks) * 100).toFixed(2) + '%' : '-'}</td>
                        <td>{money(row.spend)}</td>
                        <td>{money(perMille(row.spend, row.impressions))}</td>
                        <td>{money(perUnit(row.spend, row.clicks))}</td>
                        {hasVideo && <td>{money(perUnit(row.video_spend, row.video_completions))}</td>}
                        {hasConversions && <td>{money(perUnit(row.spend, row.conversions))}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {loadedReportType === 'url' && hasData() && (
              <div className="report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <ThSortable column="url" className="th-text">URL</ThSortable>
                      <ThSortable column="impressions">{t('reports.impressions')}</ThSortable>
                      <ThSortable column="clicks">{t('reports.clicks')}</ThSortable>
                      <ThSortable column="ctr">CTR</ThSortable>
                      <ThSortable column="conversions">{t('reports.conversions')}</ThSortable>
                      <th>CR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urlData.map((row, idx) => (
                      <tr key={idx}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td className="val-big" style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.url}
                        </td>
                        <td>{row.impressions.toLocaleString(currentLocale())}</td>
                        <td>{row.clicks.toLocaleString(currentLocale())}</td>
                        <td className={row.ctr > 0.8 ? 'val-positive' : ''}>{row.ctr.toFixed(2)}%</td>
                        <td>{row.conversions?.toLocaleString(currentLocale()) || '-'}</td>
                        <td>{row.clicks > 0 ? (((row.conversions || 0) / row.clicks) * 100).toFixed(2) + '%' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {loadedReportType === 'creative' && hasData() && (
              <div className="report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <ThSortable column="name" className="th-text">{t('reports.creative')}</ThSortable>
                      <ThSortable column="impressions">{t('reports.impressions')}</ThSortable>
                      <ThSortable column="clicks">{t('reports.clicks')}</ThSortable>
                      <ThSortable column="ctr">CTR</ThSortable>
                      <ThSortable column="conversions">{t('reports.conversions')}</ThSortable>
                      <th>CR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creativeData.map((row, idx) => (
                      <tr key={idx}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td className="val-big">{row.name}</td>
                        <td>{row.impressions.toLocaleString(currentLocale())}</td>
                        <td>{row.clicks.toLocaleString(currentLocale())}</td>
                        <td className={row.ctr > 0.8 ? 'val-positive' : ''}>{row.ctr.toFixed(2)}%</td>
                        <td>{row.conversions?.toLocaleString(currentLocale()) || '-'}</td>
                        <td>{row.clicks > 0 ? (((row.conversions || 0) / row.clicks) * 100).toFixed(2) + '%' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {loadedReportType === 'geo' && hasData() && (
              <>
                <div className="report-table-scroll">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <ThSortable column="country" className="th-text">{t('reports.country')}</ThSortable>
                        <ThSortable column="region" className="th-text">{t('reports.region')}</ThSortable>
                        <ThSortable column="impressions">{t('reports.impressions')}</ThSortable>
                        <ThSortable column="clicks">{t('reports.clicks')}</ThSortable>
                        <ThSortable column="ctr">CTR</ThSortable>
                      </tr>
                    </thead>
                    <tbody>
                      {geoData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                          <td className="val-big">{row.country}</td>
                          <td className="val-big">{row.region}</td>
                          <td>{row.impressions.toLocaleString(currentLocale())}</td>
                          <td>{row.clicks.toLocaleString(currentLocale())}</td>
                          <td className={row.ctr > 0.8 ? 'val-positive' : ''}>{row.ctr.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {loadedReportType === 'device' && hasData() && (
              <div className="report-table-scroll">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <ThSortable column="device" className="th-text">{t('reports.deviceType')}</ThSortable>
                      <ThSortable column="impressions">{t('reports.impressions')}</ThSortable>
                      <ThSortable column="clicks">{t('reports.clicks')}</ThSortable>
                      <ThSortable column="ctr">CTR</ThSortable>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceData.map((row, idx) => (
                      <tr key={idx}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td className="val-big">{row.device}</td>
                        <td>{row.impressions.toLocaleString(currentLocale())}</td>
                        <td>{row.clicks.toLocaleString(currentLocale())}</td>
                        <td className={row.ctr > 0.8 ? 'val-positive' : ''}>{row.ctr.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination for non-daily reports */}
            {loadedReportType !== 'daily' && hasData() && totalPages > 1 && (
              <div className="pagination-footer" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '20px',
                padding: '16px 0',
                borderTop: '1px solid #e9ecef'
              }}>
                <div className="pagination-info" style={{ color: '#6c757d', fontSize: '14px' }}>
                  {t('reports.showingRange', { from: ((currentPage - 1) * pageSize + 1).toLocaleString(currentLocale()), to: Math.min(currentPage * pageSize, totalCount).toLocaleString(currentLocale()), total: totalCount.toLocaleString(currentLocale()) })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-arrow"
                    style={{
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: currentPage === 1 ? '1px solid #e9ecef' : '1px solid #ddd',
                      borderRadius: '10px',
                      background: currentPage === 1 ? '#f8f9fa' : '#fff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={currentPage === 1 ? '#adb5bd' : '#FF6B4A'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`pagination-page ${currentPage === pageNum ? 'is-active' : ''}`}
                        style={{
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          borderRadius: '8px',
                          background: currentPage === pageNum ? 'linear-gradient(135deg, #FF6B4A 0%, #E94B6C 50%, #8E3A9A 100%)' : 'transparent',
                          cursor: 'pointer',
                          color: currentPage === pageNum ? '#fff' : '#495057',
                          fontSize: '14px',
                          fontWeight: currentPage === pageNum ? 600 : 400,
                          transition: 'all 0.2s'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-arrow"
                    style={{
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: currentPage === totalPages ? '1px solid #e9ecef' : '1px solid #ddd',
                      borderRadius: '10px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#fff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={currentPage === totalPages ? '#adb5bd' : '#FF6B4A'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportsPage;
